import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useUser } from '../contexts/UserContext';
import { TextField, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextareaAutosize, List, ListItem, ListItemText, IconButton, Checkbox } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

const SiteManagerComponent = () => {
    const { user } = useUser();
    const [sites, setSites] = useState([]);
    const [currentSite, setCurrentSite] = useState({ id: null, name: '', latitude: '', longitude: '', extra: '{}' });
    const [extraJSON, setExtraJSON] = useState('{}');
    const [open, setOpen] = useState(false);
    const [error, setError] = useState('');
    const [isEditMode, setIsEditMode] = useState(false);
    const [projects, setProjects] = useState([]);
    const [linkedProjects, setLinkedProjects] = useState([]);

    useEffect(() => {
        const fetchSites = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/sites`, {
                    headers: { Authorization: `Bearer ${user.token}` },
                });
                setSites(response.data);
            } catch (err) {
                setError(err.response?.data?.message || 'An error occurred while fetching sites');
            }
        };

        const fetchProjects = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/projects`, {
                    headers: { Authorization: `Bearer ${user.token}` },
                });
                setProjects(response.data);
            } catch (err) {
                setError(err.response?.data?.message || 'An error occurred while fetching projects');
            }
        };

        if (user.token) {
            fetchSites();
            fetchProjects();
        }
    }, [user]);

    useEffect(() => {
        const fetchLinkedProjects = async () => {
            if (currentSite.id) {
                try {
                    const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/project_sites/site/${currentSite.id}`, {
                        headers: { Authorization: `Bearer ${user.token}` },
                    });
                    setLinkedProjects(response.data.map(project => project.id));
                } catch (err) {
                    setError(err.response?.data?.message || 'An error occurred while fetching linked projects');
                }
            }
        };

        if (open) {
            fetchLinkedProjects();
        }
    }, [open, currentSite.id, user]);

    const handleOpen = (site) => {
        setIsEditMode(!!site);
        setCurrentSite(site || { id: null, name: '', latitude: '', longitude: '', extra: '{}' });
        setExtraJSON(site ? JSON.stringify(site.extra, null, 2) : '{}');
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setCurrentSite({ id: null, name: '', latitude: '', longitude: '', extra: '{}' });
        setExtraJSON('{}');
        setError('');
    };

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setCurrentSite({ ...currentSite, [name]: value });
    };

    const handleExtraChange = (event) => {
        const { value } = event.target;
        setExtraJSON(value);
    };

    const handleAddOrUpdateSite = async () => {
        try {
            const parsedExtra = JSON.parse(extraJSON);
            const updatedSite = { ...currentSite, extra: parsedExtra };

            if (isEditMode) {
                const response = await axios.put(`${process.env.REACT_APP_BACKEND_URL}/sites/${currentSite.id}`, updatedSite, {
                    headers: { Authorization: `Bearer ${user.token}` },
                });
                setSites(sites.map((site) => (site.id === currentSite.id ? response.data : site)));
            } else {
                const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/sites`, updatedSite, {
                    headers: { Authorization: `Bearer ${user.token}` },
                });
                setSites([...sites, response.data]);
            }
            handleClose();
        } catch (err) {
            setError(err.response?.data?.message || 'An error occurred while saving the site');
        }
    };

    const handleDelete = async (id) => {
        try {
            await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/sites/${id}`, {
                headers: { Authorization: `Bearer ${user.token}` },
            });
            setSites(sites.filter(site => site.id !== id));
        } catch (err) {
            setError(err.response?.data?.message || 'An error occurred while deleting the site');
        }
    };

    const handleProjectCheckboxChange = async (projectId) => {
        try {
            if (linkedProjects.includes(projectId)) {
                await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/project_sites/${projectId}/${currentSite.id}`, {
                    headers: { Authorization: `Bearer ${user.token}` },
                });
                setLinkedProjects(linkedProjects.filter(id => id !== projectId));
            } else {
                await axios.put(`${process.env.REACT_APP_BACKEND_URL}/project_sites/${projectId}/${currentSite.id}`, {}, {
                    headers: { Authorization: `Bearer ${user.token}` },
                });
                setLinkedProjects([...linkedProjects, projectId]);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'An error occurred while updating project links');
        }
    };

    if (!user.token) {
        return <p>You must be logged in to manage sites.</p>;
    }

    return (
        <div>
            {user.hasAnyRole(['jedimaster', 'admin']) && (
                <Button variant="contained" color="primary" onClick={() => handleOpen(null)}>
                    Add Site
                </Button>
            )}
            {error && typeof error === 'string' && <p>{error}</p>}
            <List>
                {Array.isArray(sites) && sites.map(site => (
                    <ListItem key={site.id}>
                        {user.hasAnyRole(['jedimaster', 'admin']) && (
                            <IconButton edge="end" aria-label="edit" onClick={() => handleOpen(site)} sx={{ mr: 1 }} >
                                <EditIcon />
                            </IconButton>
                        )}
                        <ListItemText primary={site.name} secondary={JSON.stringify(site.extra)} />
                        {user.hasAnyRole(['jedimaster', 'admin']) && (
                            <IconButton edge="end" aria-label="delete" onClick={() => handleDelete(site.id)}>
                                <DeleteIcon />
                            </IconButton>
                        )}
                    </ListItem>
                ))}
            </List>

            <Dialog open={open} onClose={handleClose}>
                <DialogTitle>{isEditMode ? 'Edit Site' : 'Add New Site'}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        id="name"
                        label="Name"
                        type="text"
                        fullWidth
                        name="name"
                        value={currentSite.name}
                        onChange={handleInputChange}
                    />
                    <TextField
                        margin="dense"
                        id="latitude"
                        label="Latitude"
                        type="text"
                        fullWidth
                        name="latitude"
                        value={currentSite.latitude}
                        onChange={handleInputChange}
                    />
                    <TextField
                        margin="dense"
                        id="longitude"
                        label="Longitude"
                        type="text"
                        fullWidth
                        name="longitude"
                        value={currentSite.longitude}
                        onChange={handleInputChange}
                    />
                    <TextareaAutosize
                        aria-label="extra"
                        minRows={5}
                        placeholder="Extra JSON"
                        name="extra"
                        value={extraJSON}
                        onChange={handleExtraChange}
                        style={{ width: '100%', marginTop: '16px', padding: '8px' }}
                    />
                    <List>
                        {projects.map(project => (
                            <ListItem key={project.id}>
                                <ListItemText primary={project.name} />
                                <Checkbox
                                    checked={linkedProjects.includes(project.id)}
                                    onChange={() => handleProjectCheckboxChange(project.id)}
                                />
                            </ListItem>
                        ))}
                    </List>
                    {error && <p>{error}</p>}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button onClick={handleAddOrUpdateSite}>{isEditMode ? 'Update Site' : 'Add Site'}</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default SiteManagerComponent;
